export default class CustomAdsAdapter {
  getCurrentAdBreakInfo() {
    return this._currentAdBreakInfo || null;
  }

  getCurrentAd() {
    return this._currentAd || null;
  }

  getGivenBreaks() {
    const cuePoints = this.player?.getCuePoints() || [];
    return cuePoints.length || 0;
  }

  getExpectedBreaks() {
    const cuePoints = this.player?.getCuePoints() || [];
    return cuePoints.length || 0;
  }

  getExpectedPattern() {
    if (!this.player || typeof this.player.getAllAdBreaks !== 'function') {
      return {};
    }

    const adBreaks = this.player.getAllAdBreaks();
    if (!adBreaks || adBreaks.length === 0) {
      return {};
    }

    const positionMap = {
      preroll: 'pre',
      postroll: 'post',
    };

    return adBreaks.reduce((pattern, adbreak) => {
      const position = adbreak.contentTimePosition?.toLowerCase();
      const key = positionMap[position] || 'mid';

      if (!pattern[key]) {
        pattern[key] = [];
      }
      pattern[key].push(adbreak.totalAds);

      return pattern;
    }, {});
  }

  getGivenAds() {
    return this._currentAdBreakInfo?.totalAds || 0;
  }

  getExpectedAds() {
    return this._currentAdBreakInfo?.totalAds || 0;
  }

  getAdInsertionType() {
    return this.player?.adInsertionType;
  }

  getPlayerVersion() {
    return this.player?.internalPlayerVersion;
  }

  adbreakstartListener(e) {
    this.firePlayerLog('adbreakstartListener', {});
    this._currentAdBreakInfo = e || {};
    var pos = (this._currentAdBreakInfo.contentTimePosition || '').toLowerCase();
    var adPosition = this.getNpawReference().Constants.AdPosition.Midroll;
    switch (pos) {
      case 'preroll':
        adPosition = this.getNpawReference().Constants.AdPosition.Preroll;
        break;
      case 'postroll':
        adPosition = this.getNpawReference().Constants.AdPosition.Postroll;
        break;
    }
    this.fireAdBreakStart({
      adPosition: adPosition,
    });
  }

  adbreakendListener(e) {
    this.firePlayerLog('adbreakendListener', {});
    this.fireAdBreakStop();
    this._currentAdBreakInfo = null;
  }

  adstartListener(e) {
    this.firePlayerLog('adstartListener', {});
    this._currentAd = e || {};
    // Reset quartile tracking
    this._quartilesFired = {
      first: false,
      mid: false,
      third: false,
    };

    // Reset progress
    this._currentAdProgress = 0;
    this.fireStart();
    this.fireJoin();
  }

  adendListener(e) {
    this.firePlayerLog('adendListener', {});
    this.fireStop();
    this._currentAd = null;
  }

  adskipListener(e) {
    this.firePlayerLog('adskipListener', {});
    this.fireSkip();
  }

  playbackstatechangedListener(e) {
    if (this.player.playerContext !== 'ad') {
      return;
    }
    switch (e) {
      case 'paused':
        this.adpauseListener(e);
        break;
      case 'started':
        this.adresumeListener(e);
        break;
    }
  }

  adpauseListener(e) {
    this.firePlayerLog('adpauseListener', {});
    this.firePause();
  }

  adresumeListener(e) {
    if (this.player.playerContext !== 'ad') {
      return;
    }
    this.firePlayerLog('adresumeListener', {});
    this.fireResume();
  }

  adfirstquartileListener(e) {
    this.firePlayerLog('adfirstquartileListener', {});
    this.fireQuartile(1);
  }

  admidpointListener(e) {
    this.firePlayerLog('admidpointListener', {});
    this.fireQuartile(2);
  }

  adthirdquartileListener(e) {
    this.firePlayerLog('adthirdquartileListener', {});
    this.fireQuartile(3);
  }

  aderrorListener(e, adInfo) {
    this.firePlayerLog('aderrorListener', {});
    // Mediatailor emits 'aderror' from two distinct sources:
    // 1. Ad session initialization failures (e.g. invalid/missing tracking session) - published
    //    with only the error, no adInfo. These are unique to the ad session and must be reported.
    // 2. Playback errors that occur while an ad is already playing - published with (error, adInfo).
    //    These are proxies of the underlying content player error, which is already reported once
    //    on the content playbackSession. Reporting them here too would duplicate/inflate the error
    //    count across the ad & content sessions, so we only stop the ad session for these.
    if (adInfo === undefined) {
      var code = e?.hexErrorCode || 'unknown';
      var message = e ? `${e?.hexErrorCode} (${e?.internalError?.errorCode}) | ${e?.errorMessage}` : 'Ad error';
      this.fireError(code, message);
    }
    this.fireStop({}, 'adError');
  }

  adprogressListener(adInfo, progress) {
    this._currentAdProgress = progress || 0;
    // Emit quartile events based on ad progress
    if (adInfo && adInfo.durationMs > 0 && typeof progress === 'number') {
      // Initialize quartile tracking if not exists
      if (!this._quartilesFired) {
        this._quartilesFired = {
          first: false,
          mid: false,
          third: false,
        };
      }

      // Normalize progress to milliseconds to match durationMs.
      let progressMs;
      if (progress <= 0) {
        progressMs = 0;
      } else if (progress > adInfo.durationMs / 1000) {
        // If progress is greater than duration in seconds, assume it's already in ms.
        progressMs = progress;
      } else {
        // Otherwise, assume progress is in seconds and convert to ms.
        progressMs = progress * 1000;
      }
      const progressPercentage = Math.min((progressMs / adInfo.durationMs) * 100, 100);
      // Fire first quartile at 25%
      if (progressPercentage >= 25 && !this._quartilesFired.first) {
        this._quartilesFired.first = true;
        this.adfirstquartileListener(adInfo);
      }
      // Fire midpoint at 50%
      if (progressPercentage >= 50 && !this._quartilesFired.mid) {
        this._quartilesFired.mid = true;
        this.admidpointListener(adInfo);
      }
      // Fire third quartile at 75%
      if (progressPercentage >= 75 && !this._quartilesFired.third) {
        this._quartilesFired.third = true;
        this.adthirdquartileListener(adInfo);
      }
    }
  }

  adclickListener(adUrl) {
    this.fireClick(adUrl);
  }

  registerListeners() {
    this.firePlayerLog('registerListeners - MediaTailor ads', {});
    this._currentAdBreakInfo = null;
    this._currentAd = null;
    this._adBreaksTime = [];
    this.referencesAds = {
      adbreakstart: this.adbreakstartListener.bind(this),
      adbreakend: this.adbreakendListener.bind(this),
      adstart: this.adstartListener.bind(this),
      adend: this.adendListener.bind(this),
      adskip: this.adskipListener.bind(this),
      playbackstatechanged: this.playbackstatechangedListener.bind(this),
      aderror: this.aderrorListener.bind(this),
      adprogressupdate: this.adprogressListener.bind(this),
      adclick: this.adclickListener.bind(this),
    };
    if (this.player) {
      for (var key in this.referencesAds) {
        this.player.subscribe(key, this.referencesAds[key]);
      }
    }
  }

  unregisterListeners() {
    this.firePlayerLog('unregisterListeners - MediaTailor ads', {});
    if (this.player && this.referencesAds) {
      for (var key in this.referencesAds) {
        this.player.unsubscribe(key, this.referencesAds[key]);
      }
      delete this.referencesAds;
    }
    this._currentAdBreakInfo = null;
    this._currentAd = null;
    this._adBreaksTime = [];
  }

  getPosition() {
    var ret = this.getNpawReference().Constants.AdPosition.Midroll;
    if (this._currentAd && this._currentAd.adBreakInfo && this._currentAd.adBreakInfo.contentTimePosition) {
      var pos = this._currentAd.adBreakInfo.contentTimePosition.toLowerCase();
      switch (pos) {
        case 'preroll':
          ret = this.getNpawReference().Constants.AdPosition.Preroll;
          break;
        case 'midroll':
          ret = this.getNpawReference().Constants.AdPosition.Midroll;
          break;
        case 'postroll':
          ret = this.getNpawReference().Constants.AdPosition.Postroll;
          break;
      }
    } else if (this._currentAdBreakInfo && this._currentAdBreakInfo.contentTimePosition) {
      var pos2 = this._currentAdBreakInfo.contentTimePosition.toLowerCase();
      switch (pos2) {
        case 'preroll':
          ret = this.getNpawReference().Constants.AdPosition.Preroll;
          break;
        case 'midroll':
          ret = this.getNpawReference().Constants.AdPosition.Midroll;
          break;
        case 'postroll':
          ret = this.getNpawReference().Constants.AdPosition.Postroll;
          break;
      }
    }
    return ret;
  }

  getPlayerName() {
    return 'MediaTailor AdsAdapter';
  }

  getIsSkippable() {
    return !!(
      this._currentAd &&
      (this._currentAd.isSkippable === true ||
        (this._currentAd.skipOffsetMs != null && this._currentAd.skipOffsetMs > 0))
    );
  }

  getDuration() {
    var ret = 0;
    if (this._currentAd && this._currentAd.durationMs) {
      ret = this._currentAd.durationMs / 1000;
    }
    return ret;
  }

  getTitle() {
    var ret = 'UNKNOWN';
    if (this._currentAd && this._currentAd.vastProperties && this._currentAd.vastProperties.adTitle) {
      ret = this._currentAd.vastProperties.adTitle;
    }
    return ret;
  }

  getCreativeId() {
    var ret = null;
    if (this._currentAd && this._currentAd.vastProperties && this._currentAd.vastProperties.creativeId) {
      ret = this._currentAd.vastProperties.creativeId;
    }
    return ret;
  }

  getBreaksTime() {
    return this.player?.getCuePoints() || [];
  }

  getPlayhead() {
    return this._currentAdProgress || 0;
  }

  isAdPlaying() {
    return this._currentAd !== null;
  }

  getResource() {
    return this.player?.rawPlayer?.getAssetUri() || 'UNKNOWN';
  }

  getAdId() {
    var ret = null;
    if (this._currentAd && this._currentAd.adID) {
      ret = this._currentAd.adID;
    }
    return ret;
  }

  // TODO: This is not clear and yet to be implemented.
  // getIsFullscreen() {
  //   return true;
  // }

  getCampaign() {
    var ret = null;
    if (this._currentAd && this._currentAd.vastProperties && this._currentAd.vastProperties.adServingId) {
      ret = this._currentAd.vastProperties.adServingId;
    }
    return ret;
  }

  getAdvertiser() {
    var ret = null;
    if (this._currentAd && this._currentAd.vastProperties && this._currentAd.vastProperties.advertiser) {
      ret = this._currentAd.vastProperties.advertiser;
    }
    return ret;
  }

  getAdSystem() {
    var ret = null;
    if (this._currentAd && this._currentAd.vastProperties && this._currentAd.vastProperties.adSystem) {
      ret = this._currentAd.vastProperties.adSystem;
    }
    return ret;
  }

  getCategory() {
    var ret = null;
    if (this._currentAd && this._currentAd.vastProperties && this._currentAd.vastProperties.category) {
      ret = this._currentAd.vastProperties.category;
    }
    return ret;
  }
}
